import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useCanWrite } from "@/app/auth";
import { balances, LEDGER_TYPE, type BalanceDay, type LedgerType, type TopupInput, type TopupResult } from "@/app/billing";
import { TextField } from "@/app/fields";
import { FormDrawer } from "@/app/FormDrawer";
import { ClearFilters } from "@/app/ClearFilters";
import { accounts, customers, type Account } from "@/app/master";
import { Pager } from "@/app/Pager";
import { loadingOf, nameOf, useList } from "@/app/queries";
import { useDraft } from "@/app/useDraft";
import { usePage } from "@/app/usePage";
import { useSave } from "@/app/useSave";
import { Alert, Button, EmptyState, Field, FILTER_W, Form, formatMoney, Input, InputGroup, Money, PageHeader, Select, Table, TableWrap, Th, Toolbar } from "@/ui";
import { daySpan, recentDays } from "@/app/days";
import { topupDraft, topupInput } from "./topupDraft";

/** 表格的一行：一个北京日里一种交易类型的合计，带当天的日末余额。 */
interface Row { day: string; type: LedgerType; amount: number; balance: number }

type SortKey = "day" | "amount";

const TYPES = Object.keys(LEDGER_TYPE) as LedgerType[];

/** 每天按交易类型拆成行，同一天里按 LEDGER_TYPE 的顺序（消耗、充值在前）。没有流水的日子没有行。 */
function toRows(days: BalanceDay[]): Row[] {
  return days.flatMap((d) => TYPES.filter((t) => d.amounts[t] !== undefined).map((t) => ({ day: d.day, type: t, amount: d.amounts[t]!, balance: d.balance })));
}

/** 按日期或金额排序；同值保持原顺序（新的日子在前、同一天按类型顺序）。 */
function sortRows(rows: Row[], key: SortKey, desc: boolean): Row[] {
  const cmp = key === "day" ? (a: Row, b: Row) => a.day.localeCompare(b.day) : (a: Row, b: Row) => a.amount - b.amount;
  return [...rows].sort((a, b) => (desc ? cmp(b, a) : cmp(a, b)));
}

/** 账号余额详情 /balances/{id}：选定日期范围里每个北京日（D174）按交易类型的金额与日末余额（决策 D99），可按交易类型筛选、按日期或金额排序；充值在抽屉里（docs/04 第 4 节）。 */
export function BalanceDetail() {
  const { id = "" } = useParams();
  const accountId = /^\d+$/.test(id) ? Number(id) : 0;
  const navigate = useNavigate();
  const location = useLocation();
  const canWrite = useCanWrite(); // 只读账号不显示写操作，服务端同样拒绝（docs/04 第 9 节）
  const customerList = useList("customers", customers);
  const account = useList("accounts", accounts).data?.find((a) => a.id === accountId);
  const currency = customerList.data?.find((c) => c.id === account?.customer_id)?.currency;
  const [period, setPeriod] = useState("30");
  const [dates, setDates] = useState(() => recentDays(30));
  const { from, to } = dates;
  const days = daySpan(from, to);
  const rangeError = !from || !to ? "选择完整的日期范围。" : days < 1 ? "开始日期不能晚于结束日期（结束日期晚于今天按今天算）。" : days > 366 ? "最多查询 366 天。" : undefined;
  const choosePeriod = (value: string) => { setPeriod(value); if (value !== "custom") setDates(recentDays(Number(value))); };
  const chooseDay = (key: "from" | "to", value: string) => { setPeriod("custom"); setDates((current) => ({ ...current, [key]: value })); };
  const [type, setType] = useState("");
  // 缺省是近 30 日、全部类型；改过任一项才显示清除筛选
  const filtered = period !== "30" || !!type;
  const clear = () => { choosePeriod("30"); setType(""); };
  const [sort, setSort] = useState<{ key: SortKey; desc: boolean }>({ key: "day", desc: true });
  const [topping, setTopping] = useState(false);
  const q = useQuery({ queryKey: ["balance-days", accountId, from, to], queryFn: () => balances.days(accountId, from, to), enabled: accountId > 0 && !rangeError });
  const all = toRows(q.data ?? []);
  const rows = sortRows(all.filter((r) => !type || r.type === type), sort.key, sort.desc);
  const paged = usePage("balance-days", rows, [type, sort, from, to]);
  // 下拉里总有消耗与充值，其他类型在这段时间出现过才列
  const typeOptions = TYPES.filter((t) => t === "charge" || t === "topup" || all.some((r) => r.type === t)).map((t) => ({ value: t, label: LEDGER_TYPE[t] }));
  const onSort = (key: SortKey) => setSort((s) => ({ key, desc: s.key === key ? !s.desc : true }));
  const sorted = (key: SortKey) => sort.key === key && (sort.desc ? "desc" : "asc");
  const title = account ? `${account.name} · ${nameOf(customerList.data, account.customer_id)}` : `账号 #${id}`;
  const actions = <>
    <Button onClick={() => navigate(`/balances${(location.state as { back?: string } | null)?.back ?? ""}`)}>返回</Button>
    {canWrite && <Button variant="primary" disabled={!account} onClick={() => setTopping(true)}>充值</Button>}
  </>;

  return (
    <>
      <PageHeader title={title} description="每个北京日按交易类型列出金额，消耗为负、充值为正；日末余额是当天结束时的流水账余额。" actions={actions} />
      {!accountId && <Alert tone="danger" title="加载失败">账号标识 {id} 无效</Alert>}
      {rangeError && <Alert tone="danger" title="日期范围无效">{rangeError}</Alert>}
      {q.error && <Alert tone="danger" title="加载失败">{q.error.message}</Alert>}
      <Toolbar actions={filtered && <ClearFilters onClear={clear} />}>
        <Select size="sm" width={FILTER_W.select} ariaLabel="日期范围" value={period} onChange={choosePeriod} options={[{ value: "7", label: "近 7 日（含今天）" }, { value: "30", label: "近 30 日（含今天）" }, { value: "90", label: "近 90 日（含今天）" }, { value: "custom", label: "自定义日期" }]} />
        <Input size="sm" type="date" aria-label="开始日期" value={from} onChange={(e) => chooseDay("from", e.target.value)} style={{ width: FILTER_W.date }} />
        <Input size="sm" type="date" aria-label="结束日期" value={to} onChange={(e) => chooseDay("to", e.target.value)} style={{ width: FILTER_W.date }} />
        <Select size="sm" width={FILTER_W.select} ariaLabel="交易类型" options={[{ value: "", label: "全部类型" }, ...typeOptions]} value={type} onChange={setType} />
      </Toolbar>
      <TableWrap loading={loadingOf(q)}>
        {paged.rows.length ? (
          <>
            <Table>
              <thead>
                <tr>
                  <Th w="date" sortable sorted={sorted("day")} onClick={() => onSort("day")}>日期</Th><Th w={120}>交易类型</Th>
                  <Th numeric w={176} sortable sorted={sorted("amount")} onClick={() => onSort("amount")}>金额</Th><Th numeric>日末余额</Th>
                </tr>
              </thead>
              <tbody>
                {paged.rows.map((r) => (
                  <tr key={`${r.day}:${r.type}`}>
                    <td className="ax-mono">{r.day}</td>
                    <td>{LEDGER_TYPE[r.type]}</td>
                    <td className="ax-num"><Money micro={r.amount} currency={currency} /></td>
                    <td className="ax-num"><Money micro={r.balance} currency={currency} /></td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <Pager {...paged} />
          </>
        ) : (
          !(q.isPending && accountId) && <EmptyState title="这段时间没有交易" />
        )}
      </TableWrap>
      {topping && account && <TopupDrawer account={account} currency={currency} onClose={() => setTopping(false)} />}
    </>
  );
}

/** 充值抽屉：流水已写但计数器同步失败时接口回 502，要提示不要重试（决策 D33 第 7 条）。 */
function TopupDrawer({ account, currency, onClose }: { account: Account; currency?: string; onClose: () => void }) {
  const form = useDraft(topupDraft());
  const { save, saving, error, warning, reset } = useSave((body: TopupInput) => balances.topup(account.id, body), {
    invalidate: ["balance-days", "balances"], message: "已充值", onSaved: onClose,
    partial: (e) => {
      const body = e.body as Partial<TopupResult> | undefined;
      return `流水已写入（余额 ${formatMoney(body?.balance ?? 0, body?.currency ?? currency)}），计数器未同步；不要重试充值，对账任务会按流水重建。`;
    },
  });
  const submit = () => {
    const { body, errors } = topupInput(form.draft);
    form.setErrors(errors);
    if (!body) return;
    reset();
    save(body);
  };
  return (
    <FormDrawer open title={`充值 ${account.name}`} submitLabel="充值" saving={saving} error={error} warning={warning} onSubmit={submit} onClose={onClose}>
      <Form>
        <Field label="金额" required htmlFor="t-amount" error={form.errors.amount}>
          <InputGroup addon={currency ?? "币种"}><Input id="t-amount" mono value={form.draft.amount} invalid={!!form.errors.amount} onChange={(e) => form.set("amount", e.target.value)} /></InputGroup>
        </Field>
        <TextField id="t-ref" label="充值单号" required mono value={form.draft.ref} error={form.errors.ref} onChange={(v) => form.set("ref", v)} />
        <TextField id="t-note" label="备注" value={form.draft.note} onChange={(v) => form.set("note", v)} />
      </Form>
    </FormDrawer>
  );
}
